import { Injectable, inject } from '@angular/core';
import {
  CRMActivityApiService,
  FilterGroupInterface,
  FilterGroupOperator,
  FilterOperator,
  GetActivityMetricsRequestInterface,
  MetricCountInterface,
  MetricSummaryInterface,
  MetricType,
  OwnerMetricCountsInterface,
} from '@vendasta/crm';
import { PagedListRequestInterface, PagedResponseInterface, PaginatedAPIInterface } from '@vendasta/galaxy/table';
import { BehaviorSubject, Observable, map, switchMap, tap } from 'rxjs';
import { Sort } from '@angular/material/sort';
import { IAMService } from '@vendasta/iamv2';
import { CrmInjectionToken } from '../../tokens-and-interfaces';
import { SystemFieldIds } from '../../shared-services/crm-services/field.service';
import { DateRange, UserActivity, ActivityCount, Summary } from './interfaces';
import { getMultiUsers } from '../../utils';

const AllMetricTypes: MetricType[] = [
  MetricType.METRIC_TYPE_INBOUND_CALLS,
  MetricType.METRIC_TYPE_OUTBOUND_CALLS,
  MetricType.METRIC_TYPE_EMAILS_RECEIVED,
  MetricType.METRIC_TYPE_EMAILS_SENT,
  MetricType.METRIC_TYPE_MEETINGS,
];

@Injectable()
export class ActivityLeaderboardDatasource implements PaginatedAPIInterface<UserActivity, DateRange, Sort> {
  private readonly crmActivityApiService = inject(CRMActivityApiService);
  private readonly iamService = inject(IAMService);
  private readonly config = inject(CrmInjectionToken);

  total$$ = new BehaviorSubject<ActivityCount>({});
  average$$ = new BehaviorSubject<ActivityCount>({});
  results$$ = new BehaviorSubject<UserActivity[] | undefined>(undefined);
  private readonly filter$$ = new BehaviorSubject<DateRange | undefined>(undefined);
  private readonly sort$$ = new BehaviorSubject<Sort | undefined>(undefined);

  get(req: PagedListRequestInterface<DateRange, Sort>): Observable<PagedResponseInterface<UserActivity>> {
    const filter = req?.filters?.[0];
    const sort = req?.sorting?.[0];
    const fetch$ = this.config.namespace$.pipe(
      tap(() => {
        this.total$$.next({});
        this.average$$.next({});
        this.results$$.next(undefined);
      }),
      map((namespace) => this.buildRequestParams(namespace, filter)),
      switchMap((params) => this.crmActivityApiService.getActivityMetrics(params)),
      tap((response) => {
        const summary = this.mapSummary(response?.summaries);
        this.total$$.next(summary.total);
        this.average$$.next(summary.average);
      }),
      map((response) => this.mapUsersActivities(response?.ownerMetricCounts)),
      switchMap((data) => this.fetchUserInfo(data)),
      tap((response) => {
        this.filter$$.next(filter);
        this.results$$.next(response);
      }),
    );
    const res$ =
      filter && !this.isFilterEqual(filter, this.filter$$.getValue()) ? fetch$ : this.results$$.asObservable();
    return res$.pipe(
      tap(() => (sort ? this.sort$$.next(sort) : undefined)),
      map((data) => this.sortData(data ?? [], this.sort$$.getValue())),
      map((data) => this.mapResponse(data)),
    );
  }

  private fetchUserInfo(data: UserActivity[]): Observable<UserActivity[]> {
    const userIds = data.map((item) => item.userId);
    return getMultiUsers(userIds, this.config, this.iamService).pipe(
      map((users) => {
        return data.map((item) => {
          const user = users.find((u) => u.userId === item.userId);
          const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
          return {
            ...item,
            fullName: fullName,
            profilePicture: user?.profileImageUrl,
          };
        });
      }),
    );
  }

  private sortData(data: UserActivity[], sort?: Sort): UserActivity[] {
    const field = sort?.active ?? 'name';
    const isAsc = sort?.direction !== 'desc';
    return data.sort((a, b) => {
      if (field === 'name') {
        return this.compare(a.fullName, b.fullName, isAsc);
      }
      // sort by effort and then by name if effort is equal
      const sortResult = this.compare(a.count[field], b.count[field], isAsc);
      if (sortResult !== 0) {
        return sortResult;
      }
      return this.compare(a.fullName, b.fullName, true);
    });
  }

  private compare(a: string | number, b: string | number, isAsc: boolean): number {
    if (typeof a === 'string' && typeof b === 'string') {
      return isAsc ? a.localeCompare(b) : b.localeCompare(a);
    }
    if (typeof a === 'number' && typeof b === 'number') {
      return isAsc ? a - b : b - a;
    }
    return 0;
  }

  private isFilterEqual(filterA?: DateRange, filterB?: DateRange): boolean {
    return JSON.stringify(filterA) === JSON.stringify(filterB);
  }

  private mapResponse(data: UserActivity[]): PagedResponseInterface<UserActivity> {
    return {
      data: data,
      pagingMetadata: { nextCursor: null, hasMore: false },
    };
  }

  private buildRequestParams(namespace: string, params?: DateRange): GetActivityMetricsRequestInterface {
    const filters: FilterGroupInterface = {
      operator: FilterGroupOperator.FILTER_GROUP_OPERATOR_AND,
      filters: [
        {
          fieldId: SystemFieldIds.ActivityCreated,
          operator: FilterOperator.FILTER_OPERATOR_IS_BETWEEN,
          values: [{ date: params.dateRangeStart }, { date: params.dateRangeEnd }],
        },
      ],
    };
    return {
      namespace: namespace,
      filters: filters,
      metricTypes: AllMetricTypes,
    };
  }

  private mapUsersActivities(metrics?: OwnerMetricCountsInterface[]): UserActivity[] {
    if (!metrics) {
      return [];
    }
    return metrics.map((metric) => {
      return {
        userId: metric.ownerId,
        fullName: '',
        count: this.mapMetricToCount(metric.metricCounts),
      };
    });
  }

  private mapMetricToCount(metricCount: MetricCountInterface[]): ActivityCount {
    return {
      inboundCall: this.getMetricValue(metricCount, MetricType.METRIC_TYPE_INBOUND_CALLS),
      outboundCall: this.getMetricValue(metricCount, MetricType.METRIC_TYPE_OUTBOUND_CALLS),
      inboundEmail: this.getMetricValue(metricCount, MetricType.METRIC_TYPE_EMAILS_RECEIVED),
      outboundEmail: this.getMetricValue(metricCount, MetricType.METRIC_TYPE_EMAILS_SENT),
      meeting: this.getMetricValue(metricCount, MetricType.METRIC_TYPE_MEETINGS),
    };
  }

  private mapSummary(summaries?: MetricSummaryInterface[]): Summary {
    if (!summaries) {
      return { total: {}, average: {} };
    }
    return {
      total: this.mapMetricToCount(
        summaries.map((summary) => ({ metricType: summary.metricType, count: summary.total })),
      ),
      average: this.mapMetricToCount(
        summaries.map((summary) => ({ metricType: summary.metricType, count: summary.average })),
      ),
    };
  }

  private getMetricValue(metricCount: MetricCountInterface[], metricType: MetricType): number {
    return metricCount.find((effort) => effort.metricType === metricType)?.count ?? 0;
  }
}
